import VehiclePlaceholderSvg from './assets/blankVehicle.svg';
import type { VehicleSpecificationsSection } from './store/selectors.types';

export const VEHICLE_DETAILS_SLICE_NAME = 'vehicleDetails';

// Vehicle condition values
export const VEHICLE_CONDITION_NEW = 'new';

// Mirrors InventoryStatus.Active from `dr-sales-checkin-types`; importing its runtime value drags
// in a dependency chain that breaks this project's build (see US2119857).
export const ACTIVE_INVENTORY_STATUS = 'ACTIVE';

// Warranty status values
export const WARRANTY_STATUS_ACTIVE = 'Active' as const;
export const WARRANTY_STATUS_EXPIRED = 'Expired' as const;
export const WARRANTY_STATUS_TIME_EXPIRED = 'Time expired' as const;
export const WARRANTY_STATUS_MILES_EXPIRED = 'Miles expired' as const;
export const WARRANTY_STATUS_CLASS_ACTIVE = 'active' as const;
export const WARRANTY_STATUS_CLASS_EXPIRED = 'expired' as const;

// Warranty display labels
export const WARRANTY_UNLIMITED_MILES_LABEL = 'Unlimited';
/** Value the API sends in `categoryDetails.miles` for coverage with no mileage cap */
export const WARRANTY_UNLIMITED_MILES_VALUE = 'unlimited';
/** Legacy numeric stand-in for unlimited mileage, still tolerated on inbound data */
export const WARRANTY_UNLIMITED_MILES_SENTINEL = 999999;
export const WARRANTY_TIME_REMAINING_LABEL = (years: number, months: number, totalYears: number) =>
    `${years} yr ${months} mo left / ${totalYears} yrs`;
export const WARRANTY_TIME_EXPIRED_LABEL = (monthsExpired: number, totalYears: number) =>
    `Expired ${monthsExpired} mo ago / ${totalYears} yrs`;
export const WARRANTY_MILES_REMAINING_LABEL = (remainingK: number, totalK: number) => `${remainingK}k mi left / ${totalK}k mi`;
export const WARRANTY_IN_SERVICE_DATE_LABEL = (inServiceDate: string) => `Status as of ${inServiceDate}`;
export const WARRANTY_MILES_EXPIRED_LABEL = (expiredK: number, totalK: number) => `Expired ${expiredK}k mi ago / ${totalK}k mi`;

export const TOTAL_DOTS = 16;

export const PLACEHOLDER_IMAGE = 'https://loremflickr.com/900/675/suv,car,hyundai';

export const FALLBACK_SVG = VehiclePlaceholderSvg;

export const NOT_AVAILABLE = '-';

/** Legal disclaimer pinned to the bottom of the standard features slideout */
export const VEHICLE_DATA_DISCLAIMER =
    'Vehicle data on this website is compiled from publicly available sources and believed by the publisher to be reliable. ' +
    'Vehicle data is subject to change without notice. The publisher and selling party assumes no responsibility for errors ' +
    'and/or omissions on this data and makes no representations express or implied to any actual or prospective purchaser of ' +
    'the vehicle as to the condition of the vehicle, vehicle specifications, ownership, vehicle history, ' +
    'equipment/accessories, price or warranties.';

// Standard features slideout specification sections
export const SPEC_SECTION_KEYS = {
    ENGINE: 'engine',
    TRANSMISSION: 'transmission',
    DRIVETRAIN: 'drivetrain',
    BODY: 'body',
    WEIGHTS_AND_CAPACITIES: 'weightsAndCapacities',
    FUEL_ECONOMY: 'fuelEconomy'
} as const;

/**
 * Resolves a spec's raw value to what the grid displays: missing or empty values fall back to
 * `NOT_AVAILABLE`, while a 0 (e.g. odometer) is a real reading and is kept as-is.
 */
const resolveSpecificationValue = (value?: string | number | null): string | number =>
    value != null && value !== '' ? value : NOT_AVAILABLE;

export const vehicleSpecifications = (vehicle: VehicleSpecificationsSection) =>
    [
        { label: 'Year', value: vehicle.year, testId: 'spec-year' },
        { label: 'Trans', value: vehicle.transmission, testId: 'spec-transmission' },
        { label: 'Make', value: vehicle.make, testId: 'spec-make' },
        { label: 'DriveTrain', value: vehicle.driveLine, testId: 'spec-drivetrain' },
        { label: 'Model', value: vehicle.model, testId: 'spec-model' },
        { label: 'Exterior', value: vehicle.exteriorColor, testId: 'spec-exterior' },
        { label: 'Trim', value: vehicle.trim, testId: 'spec-trim' },
        { label: 'Interior', value: vehicle.interiorColor, testId: 'spec-interior' },
        { label: 'Engine', value: vehicle.engine, testId: 'spec-engine' },
        { label: 'Odometer', value: vehicle.odometer != null ? `${vehicle.odometer} mi` : undefined, testId: 'spec-odometer' }
    ].map(({ label, value, testId }) => ({ label, testId, value: resolveSpecificationValue(value) }));
